import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  Shield,
  Phone,
  ChevronRight,
  CheckCircle2,
  Loader2,
  Building2
} from "lucide-react";
import axios from "axios";
import { trackCompanyView } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompanyDetail() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCompanyData(); }, [companyId]);

  const fetchCompanyData = async () => {
    setLoading(true);
    try {
      const companyRes = await axios.get(`${API}/companies/${companyId}`);
      setCompany(companyRes.data);
      // Track company view in Google Analytics
      trackCompanyView(companyId, companyRes.data.name);
      
      if (companyRes.data.plans && companyRes.data.plans.length > 0) {
        const planPromises = companyRes.data.plans.map(planId => 
          axios.get(`${API}/plans/${planId}`).catch(() => null)
        );
        const planResults = await Promise.all(planPromises);
        setPlans(planResults.filter(r => r !== null).map(r => r.data));
      }
    } catch (error) {
      toast.error("Failed to load company");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#A87608] animate-spin mx-auto mb-4" />
          <p className="text-[#3A4557]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!company) return null;

  return (
    <div className="min-h-screen pb-12">
      {/* Header */}
      <header className="glass-nav sticky top-4 mx-4 sm:mx-6 z-50 px-4 py-3 flex items-center justify-between rounded-2xl">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/")} className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="hidden sm:flex items-center gap-3">
            <img src={company.logo} alt={company.name} className="h-8 w-8 object-contain bg-white rounded-lg p-1" />
            <span className="font-semibold text-[#1B2A4A]">{company.name}</span>
          </div>
        </div>
        <a href="tel:+919510020907">
          <Button className="btn-gold h-10 px-5">
            <Phone className="h-4 w-4 mr-2" />
            Get Quote
          </Button>
        </a>
      </header>

      {/* Hero */}
      <section className="pt-8 pb-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center gap-8">
            <div className="h-24 w-24 rounded-2xl bg-white flex items-center justify-center p-4">
              <img src={company.logo} alt={company.name} className="max-h-16 max-w-full object-contain" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mb-2">{company.full_name || company.name}</h1>
              <p className="text-lg text-[#2E3B57]">{plans.length} {company.type === 'term' ? 'Term Insurance' : 'Health Insurance'} Plan{plans.length !== 1 ? 's' : ''} Available</p>
              {company.description && <p className="text-[#3A4557] mt-2 max-w-2xl">{company.description}</p>}
            </div>
          </div>
        </div>
      </section>

      {/* Plans */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#D4A017] to-yellow-500 flex items-center justify-center">
            <Shield className="h-5 w-5 text-[#1B2A4A]" />
          </div>
          Available Plans
        </h2>
        
        {plans.length > 0 ? (
          <div className="space-y-4">
            {plans.map((plan) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
                onClick={() => navigate(`/plan/${plan.id}`)}
                className="glass-card p-6 cursor-pointer group"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-bold text-[#1B2A4A] group-hover:text-gold transition-colors mb-1">{plan.name}</h3>
                        {plan.tagline && <span className="badge-gold text-xs">{plan.tagline}</span>}
                      </div>
                      <ChevronRight className="h-5 w-5 text-[#3A4557] group-hover:text-gold group-hover:translate-x-1 transition-all" />
                    </div>
                    <p className="text-[#3A4557] mb-4">{plan.description}</p>
                    
                    {plan.usp && plan.usp.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {plan.usp.slice(0, 4).map((usp, idx) => (
                          <span key={idx} className="badge-green text-xs">
                            <CheckCircle2 className="h-3 w-3" />
                            {usp}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {plan.variants && plan.variants.length > 0 && (
                    <div className="flex flex-wrap gap-2 md:flex-col md:items-end">
                      {plan.variants.slice(0, 3).map((variant) => (
                        <span key={variant.id} className="badge-purple text-xs">{variant.name}</span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="glass-card p-12 text-center">
            <Building2 className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
            <p className="text-[#3A4557]">No plans available yet.</p>
          </div>
        )}
      </main>

      {/* CTA */}
      <section className="mt-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto glass-card p-8 text-center">
          <h2 className="text-2xl font-bold text-[#1B2A4A] mb-3">Need Help Choosing?</h2>
          <p className="text-[#3A4557] mb-6">Our expert advisor can help you find the best plan.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+919510020907">
              <Button className="btn-gold h-12 px-8">
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </Button>
            </a>
            <Button onClick={() => navigate("/compare")} className="btn-outline h-12 px-8">
              Compare Plans
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
